/*
 * QuoteVersionDashboard.tsx
 * Tableau de bord pour gérer une version de devis.
 * Permet l'affichage des informations principales du devis (et un lien vers l'édition)
 * Ainsi que les informations spécifiques à la version du devis (et un lien vers l'édition)
 * Et les lignes de devis
 * Et des actions rapides.
 */

// Imports React
import React, { JSX, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';

// Imports FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faFileContract,
	faBriefcase,
	faCodeBranch,
	faAngleLeft,
	faPen,
	faCalendarAlt,
	faBuilding,
	faTrashAlt,
	faPlus,
} from '@fortawesome/free-solid-svg-icons';

// Imports communs
import {
	SecondaryButton,
	navigateBack,
	DefinitionTermField,
	DefinitionDescriptionField,
	VersionCard,
	StatusCard,
	PdfCard,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';

// Imports QuoteVersion
import {
	QUOTE_STATUS_CONFIG,
	QuoteVersionData,
	quotesApi,
	quotesVersionApi,
} from '../';

// Imports Tiers, Address et Contact
import { AddressData, AddressTypeData } from 'features/addresses';
import { TiersData } from 'features/tiers';
import { quoteLineApi } from '../services/quoteLineApi';

// Composant QuoteVersionDashboard
const QuoteVersionDashboard: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = React.useState<QuoteVersionData>();

	// Hooks de State (versions)
	const [versions, setVersions] = useState<QuoteVersionData[]>([]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isStable, setIsStable] = useState(false);

	// Hook de Navigation
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const statusConfig = useWorkflowStatusConfig(
		'quote_version',
		QUOTE_STATUS_CONFIG
	);

	// Fetch de la version du devis et remplissage du formData
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;
			quotesVersionApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => setLoading(false));
		}
	}, [id]);

	React.useEffect(() => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			quotesApi
				.get(formData.quote.id, token)
				.then((data) => setVersions(data.quoteVersions ?? []))
				.catch((error) => console.error(error));
			setIsStable(true);
		}
	}, [formData]);

	// handle du changement de statut du devis
	const handleStatusChange = (newStatus: string) => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			const hadProject = Boolean(formData.quote?.project?.id);
			quotesVersionApi
				.update(formData.id, { status: newStatus }, token)
				.then((updated) => {
					setFormData((prev) =>
						prev
							? {
									...prev,
									status: updated?.status ?? newStatus,
									validationWithoutOrderReason:
										updated?.validationWithoutOrderReason ??
										prev.validationWithoutOrderReason,
									quote: updated?.quote ?? prev.quote,
								}
							: prev
					);
					const projectId = updated?.quote?.project?.id;
					if (newStatus === 'VALIDATE' && !hadProject && projectId) {
						navigate(`/app/projects/${projectId}/edit`);
					}
				})
				.catch((error) => console.error(error));
		}
	};

	// Fonction pour obtenir la première adresse avec le code "FAC"
	const getFirstFacAddress = (tiers?: TiersData): JSX.Element | null => {
		const address = tiers?.addresses?.find((addr: AddressData) =>
			addr.types.some((type: AddressTypeData) => type.code === 'FAC')
		);
		if (!address) return null;
		return (
			<>
				{address.line1}
				<br />
				{address.line2 && (
					<>
						{address.line2}
						<br />
					</>
				)}
				{address.zipCode} {address.city}
				<br />
				{address.country}
			</>
		);
	};

	const handleAddVersion = () => {
		setIsAddDialogOpen(false);

		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		quotesVersionApi
			.duplicate(formData.id, token)
			.then((newVersion) => {
				setVersions((prev) => [...prev, newVersion]);
				navigate(`/app/quoteVersions/${newVersion.id}/manage`);
			})
			.catch(console.error);
	};

	const handleDeleteVersion = () => {
		setIsDeleteDialogOpen(false);

		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		quotesVersionApi
			.delete(formData.id, token)
			.then(() => {
				const remaining = versions.filter((v) => v.id !== formData.id);
				setVersions(remaining);

				if (remaining.length > 0) {
					const last = remaining[remaining.length - 1];
					navigate(`/app/quoteVersions/${last.id}/manage`);
				} else {
					navigate('/app/quotes');
				}
			})
			.catch(console.error);
	};

	const handleNavigateVersion = (id: number) => {
		navigate(`/app/quoteVersions/${id}/manage`);
	};

	const handleDeleteQuoteLine = (lineId: number) => {
		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		quoteLineApi
			.delete(lineId, token)
			.then(() => {
				setFormData((prev) =>
					prev
						? {
								...prev,
								quoteLines: prev.quoteLines.filter(
									(line) => line.id !== lineId
								),
							}
						: prev
				);
			})
			.catch(console.error);
	};

	// Rendu du composant
	return (
		<div>
			{/* Grille haute : 3 colonnes (blocs Dates, versions et statut) */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-3 mb-6">
				{/* CARD Contenant les informations des dates (date de création, date de début et de fin) */}
				<div className=" p-6 bg-white rounded-lg shadow-md">
					<h2 className="text-2xl font-bold mb-4">
						<FontAwesomeIcon
							icon={faCalendarAlt}
							className="w-6 h-6 text-gray-700"
						/>{' '}
						Dates :
					</h2>
					<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
						<DefinitionTermField label="Création :" />
						<DefinitionDescriptionField
							value={
								formData?.createdAt
									? new Date(formData.createdAt).toLocaleDateString('fr-FR')
									: '—'
							}
							loading={loading}
						/>
						<DefinitionTermField label="Édition :" />
						<DefinitionDescriptionField
							value={
								formData?.editedAt
									? new Date(formData.editedAt).toLocaleDateString('fr-FR')
									: 'Non définie'
							}
							loading={loading}
						/>
						<DefinitionTermField label="Validité :" />
						<DefinitionDescriptionField
							value={
								'Du ' +
									(new Date(formData?.startDate ?? '').toLocaleDateString(
										'fr-FR'
									) || '—') +
									' au ' +
									new Date(formData?.endDate ?? '').toLocaleDateString(
										'fr-FR'
									) || '—'
							}
							loading={loading}
						/>
						<DefinitionTermField label="Activités :" />
						<DefinitionDescriptionField
							value={
								'Du ' +
									(new Date(
										formData?.activityStartDate ?? ''
									).toLocaleDateString('fr-FR') || '—') +
									' au ' +
									new Date(formData?.activityEndDate ?? '').toLocaleDateString(
										'fr-FR'
									) || '—'
							}
							loading={loading}
						/>
					</dl>
				</div>

				{/* CARD Contenant les informations des versions de devis */}
				<VersionCard
					icon={faCodeBranch}
					title="Versions"
					formData={formData}
					versions={versions}
					loading={loading}
					isStable={isStable}
					onNavigate={handleNavigateVersion}
					onAdd={handleAddVersion}
					onDelete={handleDeleteVersion}
					isDeleteDialogOpen={isDeleteDialogOpen}
					setIsDeleteDialogOpen={setIsDeleteDialogOpen}
					isAddDialogOpen={isAddDialogOpen}
					setIsAddDialogOpen={setIsAddDialogOpen}
				/>

				<StatusCard
					status={formData?.status}
					config={statusConfig}
					onStatusChange={handleStatusChange}
					loading={loading}
				/>

				{/* Deuxième ligne : 2 colonnes (informations principales à gauche, affaires et autres versions à droite) */}

				{/* CARDS Informations principales */}
				<div className="col-span-2 p-6 bg-white rounded-lg shadow-md">
					{/* Ligne d'entête */}
					<div className="flex items-center mb-4">
						{/* Avatar rond avec icon faFileContract*/}
						{/* Test si le type de tiers est CLI */}

						<div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4">
							<FontAwesomeIcon
								icon={faFileContract}
								className="w-7 h-7 text-gray-50"
							/>
						</div>

						{/* Sinon, Placeholder pendant le chargement */}
						<h1 className="text-3xl font-bold mr-4">
							{loading ? (
								<Skeleton
									className="w-80 h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
									style={{
										backgroundSize: '200% 100%',
										animation: 'shimmer 1.5s infinite linear',
									}}
								/>
							) : (
								'Devis ' + formData?.quote.referenceNumber
							)}
						</h1>
					</div>

					{/* Informations du devis */}
					<div className="pt-4 pb-2">
						<p>
							<b>Informations principales : </b>
							{/* Icon crayon permettant de naviguer vers /app/quotes/${formData?.quote.id}/edit */}
							<FontAwesomeIcon
								icon={faPen}
								className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
								onClick={() => {
									navigate(`/app/quotes/${formData?.quote.id}/edit`);
								}}
							/>
							<div className="grid grid-cols-2 gap-2">
								{/* Labels à gauche, données à droite, les colonnes sont de largeur minimale */}
								<div>
									<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
										<DefinitionTermField label="Notre référence :" />
										<DefinitionDescriptionField
											value={formData?.quote.referenceNumber || '—'}
											loading={loading}
										/>
										<DefinitionTermField label="Classification :" />
										<DefinitionDescriptionField
											value={formData?.quote.classification?.label || '—'}
											loading={loading}
										/>
									</dl>
								</div>

								<div>
									<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
										<DefinitionTermField label="Budget :" />
										<DefinitionDescriptionField
											value={formData?.quote.budgeted ? 'Oui' : 'Non'}
											loading={loading}
										/>
									</dl>
								</div>
							</div>
						</p>
					</div>

					{/* Informations de la version du devis */}
					<div className="pt-4 pb-2">
						<p>
							<b>Informations de version : </b>
							{/* Icon crayon permettant de naviguer vers /app/quoteVersions/${formData?.id}/edit */}
							<FontAwesomeIcon
								icon={faPen}
								className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
								onClick={() => {
									navigate(`/app/quoteVersions/${formData?.id}/edit`);
								}}
							/>
						</p>
						<div className="grid grid-cols-2 gap-2 mt-2">
							{/* Labels à gauche, données à droite, les colonnes sont de largeur minimale */}
							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									{/* Pilote : pilote interne */}
									<DefinitionTermField label="Pilote :" />
									<DefinitionDescriptionField
										value={
											formData?.assignedUser
												? formData?.assignedUser?.firstName +
														' ' +
														formData?.assignedUser?.name || '—'
												: '—'
										}
										loading={loading}
									/>
									{/* Propriétaire maquette */}
									{formData?.quote.classification &&
									['D', 'K', 'Q', 'T'].includes(
										formData.quote.classification.code
									) ? (
										<>
											<DefinitionTermField
												label="Propriétaire :"
												tooltip="Propriétaire de la maquette"
											/>
											<DefinitionDescriptionField
												value={formData?.relatedTiers?.label || '—'}
												loading={loading}
											/>
										</>
									) : null}
								</dl>
							</div>

							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									{/* Exercice : pour l'analyse des Résultats (pas du point de vue comptabilité) */}
									<DefinitionTermField
										label="Exercice :"
										tooltip="L'exercice pour l'analyse des Résultats (pas du point de vue comptabilité)"
									/>
									<DefinitionDescriptionField
										value={formData?.fiscalYear || '—'}
										loading={loading}
									/>
									{/* Autorisation du propriétaire (booléen) */}
									{formData?.quote.classification &&
									['D', 'K', 'Q', 'T'].includes(
										formData.quote.classification.code
									) ? (
										<>
											<DefinitionTermField
												label="Autorisation :"
												tooltip="Autorisation du propriétaire"
											/>
											<DefinitionDescriptionField
												value={formData?.relatedApproval ? 'Oui' : 'Non'}
												loading={loading}
											/>
										</>
									) : null}
								</dl>
							</div>
						</div>

						<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 mt-4">
							{/* Votre référence : Référence du client pour ce devis */}
							<DefinitionTermField
								label="Votre référence :"
								tooltip="La référence du client pour ce devis"
							/>
							<DefinitionDescriptionField
								value={formData?.tiersReference || '—'}
								loading={loading}
							/>

							{/* Objet : label du devis */}
							<DefinitionTermField
								label="Objet :"
								tooltip="Un label pour le devis, personnalisable à chaque version."
							/>
							<DefinitionDescriptionField
								value={formData?.commentIndex || '—'}
								loading={loading}
							/>

							{/* Signataire : label du devis */}
							<DefinitionTermField
								label="Signataire"
								tooltip="Le ou les signataire(s) de cette version du devis."
							/>
							<DefinitionDescriptionField
								value={formData?.signatory || '—'}
								loading={loading}
							/>

							{/* Notes de contexte : exemple Environnement et Maquettes */}
							<DefinitionTermField
								label="Environnement et maquettes :"
								tooltip="Notes de contexte pour le devis. Exemple : Texte Environnement et Maquettes utilisées"
							/>
							<DefinitionDescriptionField
								value={formData?.contextNotes || '—'}
								loading={loading}
							/>

							{/* Texte type : Texte inséré automatiquement dans les devis, sélectionnable parmi une liste préalablement définie. */}
							<DefinitionTermField
								label="Texte type :"
								tooltip="Texte inséré dans le devis, sélectionnable parmi une liste préalablement définie."
							/>
							<DefinitionDescriptionField
								value={formData?.generatedText || '—'}
								loading={loading}
							/>

							{/* Observations : texte libre pour ajouter des informations qui apparaitront sur le devis */}
							<DefinitionTermField
								label="Observations :"
								tooltip="Observations (ou notes publiques) : texte libre pour ajouter des informations qui apparaitront en bas du devis."
							/>
							<DefinitionDescriptionField
								value={formData?.publicNotes || '—'}
								loading={loading}
							/>
							<DefinitionTermField label="Validation sans commande :" />
							<DefinitionDescriptionField
								value={formData?.validationWithoutOrderReason || '—'}
								loading={loading}
							/>
						</dl>
					</div>
					<div className="pt-4 pb-2">
						<p>
							<b>Lignes du devis</b>
							<button
								className="text-gray-400 hover:text-green-800"
								onClick={() => {
									navigate(
										`/app/quoteLines/new?quoteVersionId=${formData?.id}`
									);
								}}
							>
								<FontAwesomeIcon icon={faPlus} className="ml-2" />
							</button>
							{/* Tableau des lignes du devis */}
							<div className="overflow-x-auto mt-2">
								<table className="min-w-full border border-gray-200 rounded-lg text-sm">
									<thead className="bg-gray-100">
										<tr>
											<th className="px-3 py-2 border-b text-left">
												Description
											</th>
											<th className="px-3 py-2 border-b text-right">PU</th>
											<th className="px-3 py-2 border-b text-right">Jours</th>
											<th className="px-3 py-2 border-b text-right">
												Participants
											</th>
											<th className="px-3 py-2 border-b text-right">Qté</th>
											<th className="px-3 py-2 border-b text-right">
												Total HT
											</th>
											<th className="px-3 py-2 border-b text-right">Actions</th>
										</tr>
									</thead>
									<tbody>
										{loading ? (
											// Skeleton quand cela charge
											[1, 2, 3].map((i) => (
												<tr key={i} className="animate-pulse">
													<td className="px-3 py-2 border-b">
														<div className="h-4 bg-gray-200 rounded w-40"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-10 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-8 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-12 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b"></td>
													<td className="px-3 py-2 border-b"></td>
													<td className="px-3 py-2 border-b"></td>
												</tr>
											))
										) : formData?.quoteLines &&
										  formData.quoteLines.length > 0 ? (
											<>
												{formData.quoteLines.map((line) => (
													<tr key={line.id}>
														<td className="px-3 py-2 border-b">
															{line.description}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.unitPrice.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.nbDays ?? '—'}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.nbParticipants ?? '—'}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.quantity}
														</td>
														<td className="px-3 py-2 border-b text-right font-semibold">
															{line.total.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
														</td>
														<td className="px-3 py-2 border-b text-right">
															<FontAwesomeIcon
																icon={faPen}
																className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-blue-600"
																onClick={() => {
																	navigate(`/app/quoteLines/${line.id}/edit`);
																}}
															/>
															<FontAwesomeIcon
																icon={faTrashAlt}
																className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-red-600"
																onClick={() => {
																	handleDeleteQuoteLine(line.id);
																}}
															/>
														</td>
													</tr>
												))}
												<tr className="bg-gray-100">
													<td
														className="px-3 py-3 border-b text-left font-bold"
														colSpan={5}
													>
														Total
													</td>
													<td
														className="px-3 py-3 border-b text-right font-bold"
														colSpan={1}
													>
														{formData.quoteLines
															.reduce(
																(acc, line) =>
																	acc + line.quantity * (line.unitPrice ?? 0),
																0
															)
															.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
													</td>
													<td className="px-3 py-3 border-b"></td>
												</tr>
											</>
										) : (
											<tr>
												<td
													className="px-3 py-3 border-b text-center text-gray-500"
													colSpan={6}
												>
													Aucune ligne dans ce devis.
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</p>
					</div>

					<div className="flex justify-end mt-4 space-x-2">
						<SecondaryButton
							onClick={() =>
								navigateBack(navigate, () => navigate('/app/quotes'))
							}
							label="Retour"
							size="lg"
							icon={faAngleLeft}
						/>
					</div>
				</div>

				<div className="col-span-1 space-y-4">
					{/* CARD AFFAIRE alignement vertical */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faBriefcase}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Affaire :
						</h2>
						{formData?.quote.project?.referenceNumber ? (
							<a
								href={`/app/projects/${formData.quote.project.id}/edit`}
								className="text-gray-600 hover:underline hover:text-blue-600"
							>
								{formData.quote.project.referenceNumber}
							</a>
						) : (
							<p>Aucune affaire liée.</p>
						)}
					</div>

					{/* CARD CLIENT */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faBuilding}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Client :
						</h2>
						<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
							<DefinitionTermField label="Nom :" />

							<DefinitionDescriptionField
								value={formData?.quote.tiers?.label || '—'}
								loading={loading}
								onClick={() => {
									location.href = `/app/tiers/${formData?.quote.tiers?.id}/manage`;
								}}
							/>

							<DefinitionTermField label="Adresse :" />
							{/* On selectionne la première adresse trouvée ayant le code FAC */}
							<dd>{getFirstFacAddress(formData?.quote.tiers)}</dd>
						</dl>
						<dl className="mt-4">
							<DefinitionTermField label="Chargé d'affaire :" />
							<dd className="text-sm break-words whitespace-normal">
								{formData?.businessManager ? (
									<>
										<div>
											{formData.businessManager.firstName}{' '}
											{formData.businessManager.lastName}
										</div>
										<div>{formData.businessManager.email}</div>
										<div>{formData.businessManager.workPhone}</div>
										<div>{formData.businessManager.mobilePhone}</div>
									</>
								) : (
									'Non sélectionné'
								)}
							</dd>
						</dl>
					</div>

					<PdfCard
						resource="quote-versions"
						entity={formData}
						onEntityUpdate={(updated) => setFormData(updated)}
						loading={loading}
						size="md"
						className="w-full"
					/>
				</div>
			</div>
		</div>
	);
};
export default QuoteVersionDashboard;
